import { Link } from 'react-router-dom';
import type { Video } from '@/types/video';
import { formatCount, formatDuration, timeAgo } from '@/utils/video';
import { useFavorites } from '@/hooks/useFavorites';

interface VideoCardProps {
  video: Video;
  showFavorite?: boolean;
  showRemove?: boolean;
  onRemove?: (id: string) => void;
}

export default function VideoCard({
  video,
  showFavorite = true,
  showRemove = false,
  onRemove,
}: VideoCardProps) {
  const { isFavorite, toggleFavorite } = useFavorites();
  const favorite = isFavorite(video.id);

  return (
    <article className="group relative flex flex-col overflow-hidden rounded-lg border border-background-200/70 bg-background-100 transition-colors hover:border-primary-700">
      <Link to={`/detail/${video.id}`} className="relative block aspect-video w-full overflow-hidden">
        <img
          src={video.cover}
          alt={`${video.title} - 成人快手短视频封面`}
          title={`${video.title} - 成人快手`}
          className="h-full w-full object-top object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
        <span className="absolute bottom-2 right-2 rounded-md bg-black/70 px-2 py-0.5 text-xs font-medium text-white">
          {formatDuration(video.duration)}
        </span>
        <span className="absolute left-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-primary-500/90 text-foreground-950 opacity-0 transition-opacity group-hover:opacity-100">
          <i className="ri-play-fill text-base" />
        </span>
      </Link>

      <div className="flex flex-1 flex-col p-4">
        <Link to={`/detail/${video.id}`}>
          <h3 className="line-clamp-2 font-heading text-sm font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-300 md:text-base">
            {video.title}
          </h3>
        </Link>

        <div className="mt-3 flex items-center gap-2 text-xs text-foreground-500">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-secondary-500/20 text-secondary-300">
            <i className="ri-user-3-line text-xs" />
          </span>
          <span className="truncate">{video.author}</span>
          <span className="ml-auto flex items-center gap-1 whitespace-nowrap">
            <i className="ri-play-circle-line" />
            {formatCount(video.views)}
          </span>
        </div>

        <div className="mt-3 flex items-center justify-between">
          <span className="text-xs text-foreground-400">{timeAgo(video.publishDate)}</span>

          <div className="flex items-center gap-1">
            {showRemove && (
              <button
                type="button"
                onClick={() => onRemove?.(video.id)}
                className="flex h-8 w-8 items-center justify-center rounded-full text-foreground-400 transition-colors hover:bg-background-200 hover:text-primary-300"
                aria-label="移除收藏"
                title="移除收藏"
              >
                <i className="ri-delete-bin-6-line text-base" />
              </button>
            )}
            {showFavorite && (
              <button
                type="button"
                onClick={() => toggleFavorite(video.id)}
                className={`flex h-8 w-8 items-center justify-center rounded-full transition-colors ${
                  favorite
                    ? 'bg-primary-500/20 text-primary-300'
                    : 'text-foreground-400 hover:bg-background-200 hover:text-primary-300'
                }`}
                aria-label={favorite ? '取消收藏' : '收藏视频'}
                title={favorite ? '取消收藏' : '收藏视频'}
              >
                <i className={favorite ? 'ri-heart-3-fill text-base' : 'ri-heart-3-line text-base'} />
              </button>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}